import type { BenchView } from '@gamolution/shared';

export interface ModelBenchHeaderProps {
  title: string;
  subtitle: string;
  tickLabel: string;
  tick: number;
  playing: boolean;
  statusLabel?: string | undefined;
  primaryActionLabel?: string | undefined;
  onInterveneResume: () => void;
  onOpenUtility: () => void;
  tweaksOpen: boolean;
  onOpenTweaks: () => void;
  benchViews: readonly { id: BenchView; label: string; enabled: boolean }[];
  benchView: BenchView;
  onBenchViewChange: (view: BenchView) => void;
  layersUnavailableReason?: string;
  showViewToggle: boolean;
}

export function ModelBenchHeader({
  title,
  subtitle,
  tickLabel,
  tick,
  playing,
  statusLabel: statusOverride,
  primaryActionLabel,
  onInterveneResume,
  onOpenUtility,
  tweaksOpen,
  onOpenTweaks,
  benchViews,
  benchView,
  onBenchViewChange,
  layersUnavailableReason,
  showViewToggle,
}: ModelBenchHeaderProps) {
  const statusLabel = statusOverride ?? (playing
    ? `LIVE · ${tickLabel} +${tick}`
    : `PAUSED · ${tickLabel} +${tick}`);

  return (
    <header className="cn-header">
      <div className="cn-header__brand">
        <span className="cn-logo">M</span>
        <div className="cn-header__titles">
          <div className="cn-title">{title.toUpperCase()}</div>
          <div className="cn-subtitle">{subtitle.toUpperCase()}</div>
        </div>
      </div>

      <div className="cn-header__spacer" />

      {showViewToggle && (
        <div className="cn-header__center">
          <div className="cn-segment">
            {benchViews.map((v) => (
              <button
                key={v.id}
                type="button"
                className={benchView === v.id && v.enabled ? 'cn-segment__btn--active' : 'cn-segment__btn'}
                disabled={!v.enabled}
                title={!v.enabled && v.id === 'layers' ? layersUnavailableReason : undefined}
                onClick={() => onBenchViewChange(v.id)}
              >
                {v.label}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="cn-header__actions">
        <span className={`cn-live${playing && statusOverride === undefined ? '' : ' cn-live--paused'}`}>
          <span className="cn-live__dot" />
          {statusLabel}
        </span>
        <button type="button" className="cn-btn cn-btn--primary" onClick={onInterveneResume}>
          {primaryActionLabel ?? (playing ? 'Intervene' : 'Resume')}
        </button>
        {!tweaksOpen && (
          <button type="button" className="cn-btn--icon" onClick={onOpenTweaks} aria-label="Tweaks">
            <svg className="cn-icon-sliders" width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
              <line x1="2" y1="3.5" x2="12" y2="3.5" stroke="currentColor" strokeWidth="1.2" />
              <circle cx="8" cy="3.5" r="1.6" fill="currentColor" />
              <line x1="2" y1="10.5" x2="12" y2="10.5" stroke="currentColor" strokeWidth="1.2" />
              <circle cx="5" cy="10.5" r="1.6" fill="currentColor" />
            </svg>
          </button>
        )}
        <button type="button" className="cn-btn--icon" onClick={onOpenUtility} aria-label="Utility menu">
          ⋯
        </button>
      </div>
    </header>
  );
}
